import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../../../config/env";
import sondertagService from "../services/sondertagService";

export default function useSondertagSocket({
  // SCHOOLYEAR
  setSchoolyears,
  setSelectedSchoolyear,
  selectedSchoolyearIdRef,

  // EVENTS
  setSonderEvents,
  setSelectedSonderEvent,
  selectedSonderEventIdRef,

  // MAIN STRUCTURE ✅ (UPDATED)
  loadSonderEventData,
  loadTeacherWorkloads,
  setSondertage,
  setStatsAngebote,
}) {
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 2000,
    });

    socketRef.current = socket;

    /**
     * =========================================================
     * ✅ SCHOOLYEAR EVENTS
     * =========================================================
     */

    socket.on("schoolyearCreated", async () => {
      const res = await sondertagService.getSchoolyears();
      setSchoolyears(res);
    });

    socket.on("schoolyearUpdated", async ({ schoolyearId }) => {
      if (schoolyearId !== selectedSchoolyearIdRef.current) return;

      const res = await sondertagService.getSchoolyears();
      setSchoolyears(res);

      const updated = res.find((s) => s.id === schoolyearId);
      if (updated) setSelectedSchoolyear(updated);
    });

    /**
     * =========================================================
     * ✅ SONDERTAG EVENTS
     * =========================================================
     */

    socket.on("sonderEventCreated", async ({ schoolyearId }) => {
      if (Number(schoolyearId) !== Number(selectedSchoolyearIdRef.current))
        return;

      const res = await sondertagService.getSonderEvents(schoolyearId);
      setSonderEvents(res);
    });

    socket.on("sondertagCreated", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedSonderEventIdRef.current)) return;

      const res = await sondertagService.getSonderEventData(eventId);
      setSonderEvents(res);
    });

    socket.on("sondertagUpdated", ({ sondertag }) => {
      if (Number(sondertag.id) !== Number(selectedSonderEventIdRef.current))
        return;

      setSelectedSonderEvent((prev) => ({
        ...prev,
        ...sondertag,
      }));
    });

    socket.on("sonderEventDeleted", ({ eventId }) => {
      if (!eventId) return;

      if (Number(eventId) === Number(selectedSonderEventIdRef.current)) {
        setSelectedSonderEvent(null);
      }

      loadSonderEventData(selectedSchoolyearIdRef.current);
    });

    /**
     * =========================================================
     * ✅ DAY EVENTS (formerly sondertage)
     * =========================================================
     */

    socket.on("dayCreated", async ({ eventId }) => {
      if (eventId !== selectedSonderEventIdRef.current) return;
      await loadSonderEventData(eventId);
    });

    socket.on("dayUpdated", async ({ eventId }) => {
      if (eventId !== selectedSonderEventIdRef.current) return;
      await loadSonderEventData(eventId);
    });

    socket.on("dayDeleted", async ({ eventId }) => {
      if (eventId !== selectedSonderEventIdRef.current) return;
      await loadSonderEventData(eventId);
    });

    /**
     * =========================================================
     * ✅ JAHRGANG EVENTS
     * =========================================================
     */

    socket.on("jahrgangCreated", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedSonderEventIdRef.current)) return;

      await loadSonderEventData(eventId);
    });

    socket.on("jahrgangUpdated", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedSonderEventIdRef.current)) return;

      await loadSonderEventData(eventId);
    });

    socket.on("jahrgangDeleted", async ({ eventId }) => {
      if (Number(eventId) !== Number(selectedSonderEventIdRef.current)) return;

      await loadSonderEventData(eventId);
    });

    /**
     * =========================================================
     * ✅ ANGEBOT EVENTS (LOCK + RESERVE ✅)
     * =========================================================
     */

    // 🔒 LOCK
    socket.on(
      "sonderAngebotLocked",
      ({ angebotId, lockedBy, locked_byUserAbbreviation }) => {
        setSondertage((prev) =>
          prev.map((day) => ({
            ...day,
            jahrgaenge: day.jahrgaenge.map((j) => ({
              ...j,
              angebote: j.angebote.map((a) =>
                a.id === angebotId
                  ? { ...a, lockedBy, locked_byUserAbbreviation }
                  : a,
              ),
            })),
          })),
        );
      },
    );

    // 🔓 UNLOCK
    socket.on("sonderAngebotUnlocked", ({ angebotId }) => {
      setSondertage((prev) =>
        prev.map((day) => ({
          ...day,
          jahrgaenge: day.jahrgaenge.map((j) => ({
            ...j,
            angebote: j.angebote.map((a) =>
              a.id === angebotId
                ? { ...a, lockedBy: null, locked_byUserAbbreviation: null }
                : a,
            ),
          })),
        })),
      );
    });

    // ✅ BULK UPDATE (reserve / unreserve / edit)
    socket.on("sonderAngeboteChanged", ({ angebote }) => {
      const updateMap = new Map(angebote.map((a) => [a.id, a]));

      // ✅ update event view
      setSondertage((prev) =>
        prev.map((day) => ({
          ...day,
          jahrgaenge: day.jahrgaenge.map((j) => ({
            ...j,
            angebote: j.angebote.map((a) =>
              updateMap.has(a.id) ? { ...a, ...updateMap.get(a.id) } : a,
            ),
          })),
        })),
      );

      // ✅ ✅ update stats source
      setStatsAngebote((prev) =>
        prev.map((a) =>
          updateMap.has(a.id) ? { ...a, ...updateMap.get(a.id) } : a,
        ),
      );
    });

    // ✅ DELETE
    socket.on("sonderAngebotDeleted", ({ angebotId, eventId }) => {
      if (Number(eventId) !== Number(selectedSonderEventIdRef.current)) return;

      if (!angebotId) return;

      setSondertage((prev) =>
        prev.map((day) => ({
          ...day,
          jahrgaenge: day.jahrgaenge.map((j) => ({
            ...j,
            angebote: j.angebote.filter((a) => a.id !== angebotId),
          })),
        })),
      );
    });

    socket.on("teacherStatCreated", async ({ schoolyearId }) => {
      if (selectedSchoolyearIdRef.current !== schoolyearId) {
        return;
      }

      await loadTeacherWorkloads(schoolyearId);
    });

    socket.on("teacherStatUpdated", async ({ schoolyearId }) => {
      if (selectedSchoolyearIdRef.current !== schoolyearId) {
        return;
      }
      await loadTeacherWorkloads(schoolyearId);
    });

    socket.on("teacherStatDeleted", async ({ schoolyearId }) => {
      if (selectedSchoolyearIdRef.current !== schoolyearId) {
        return;
      }

      await loadTeacherWorkloads(schoolyearId);
    });

    /**
     * =========================================================
     * ✅ CLEANUP
     * =========================================================
     */

    return () => {
      socket.disconnect();
    };
  }, []);
}
